<template>
  <div class="home-dashboard-page">
    <!-- 顶部标题区 -->
    <div class="hero-panel">
      <div class="hero-left">
        <div class="hero-title">智能回收箱数据总览</div>
        <div class="hero-subtitle">设备运行、清运趋势、垃圾占比、社区排行、车辆排行</div>
      </div>

      <div class="hero-right">
        <el-date-picker
          v-model="dateRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          value-format="timestamp"
          format="yyyy-MM-dd HH:mm:ss"
          :default-time="['00:00:00', '23:59:59']"
          class="date-range"
        />
        <el-button type="primary" icon="el-icon-search" :loading="loadingAll" @click="fetchAllData">
          刷新数据
        </el-button>
        <el-button icon="el-icon-refresh" @click="resetToday">今日</el-button>
      </div>
    </div>

    <!-- 总览统计 -->
    <div class="overview-grid">
      <div class="overview-card">
        <div class="overview-label">设备总数</div>
        <div class="overview-value">{{ overview.device_total }}</div>
      </div>
      <div class="overview-card">
        <div class="overview-label">车辆总数</div>
        <div class="overview-value">{{ overview.vehicle_total }}</div>
      </div>
      <div class="overview-card">
        <div class="overview-label">在线车辆</div>
        <div class="overview-value success">{{ overview.vehicle_online_total }}</div>
      </div>
      <div class="overview-card">
        <div class="overview-label">清运中车辆</div>
        <div class="overview-value success">{{ overview.vehicle_collecting_total }}</div>
      </div>
      <div class="overview-card">
        <div class="overview-label">空闲车辆</div>
        <div class="overview-value warning">{{ overview.vehicle_idle_total }}</div>
      </div>
      <div class="overview-card">
        <div class="overview-label">累计清运次数</div>
        <div class="overview-value">{{ overview.collect_total }}</div>
      </div>
      <div class="overview-card">
        <div class="overview-label">今日清运次数</div>
        <div class="overview-value">{{ overview.collect_today }}</div>
      </div>
      <div class="overview-card">
        <div class="overview-label">累计快照重量</div>
        <div class="overview-value">{{ formatWeight(overview.room_weight_total) }}<span>kg</span></div>
      </div>
      <div class="overview-card">
        <div class="overview-label">今日快照重量</div>
        <div class="overview-value">{{ formatWeight(overview.room_weight_today) }}<span>kg</span></div>
      </div>
      <div class="overview-card">
        <div class="overview-label">累计过磅重量</div>
        <div class="overview-value">{{ formatWeight(overview.weigh_weight_total) }}<span>kg</span></div>
      </div>
      <div class="overview-card">
        <div class="overview-label">今日过磅重量</div>
        <div class="overview-value">{{ formatWeight(overview.weigh_weight_today) }}<span>kg</span></div>
      </div>
      <div class="overview-card">
        <div class="overview-label">未审核过磅单</div>
        <div class="overview-value danger">{{ overview.weigh_unaudited_total }}</div>
      </div>
    </div>

    <!-- 主体 -->
    <div class="main-grid">
      <!-- 左上：近7天趋势 -->
      <div class="panel-card">
        <div class="panel-header">
          <span>近{{ trendDays }}天清运趋势</span>
          <el-button type="text" @click="fetchCollectTrend">刷新</el-button>
        </div>

        <div v-if="trendItems.length" class="trend-list">
          <div v-for="(item, index) in trendItems" :key="index" class="trend-row">
            <div class="trend-date">{{ item.date }}</div>

            <div class="trend-item">
              <span class="trend-label">清运次数</span>
              <div class="bar-bg">
                <div
                  class="bar-inner blue"
                  :style="{ width: getBarWidth(item.collect_count, trendItems, 'collect_count') }"
                ></div>
              </div>
              <span class="trend-value">{{ item.collect_count || 0 }}</span>
            </div>

            <div class="trend-item">
              <span class="trend-label">快照重量</span>
              <div class="bar-bg">
                <div
                  class="bar-inner cyan"
                  :style="{ width: getBarWidth(item.room_weight, trendItems, 'room_weight') }"
                ></div>
              </div>
              <span class="trend-value">{{ formatWeight(item.room_weight) }}</span>
            </div>

            <div class="trend-item">
              <span class="trend-label">过磅重量</span>
              <div class="bar-bg">
                <div
                  class="bar-inner green"
                  :style="{ width: getBarWidth(item.weigh_weight, trendItems, 'weigh_weight') }"
                ></div>
              </div>
              <span class="trend-value">{{ formatWeight(item.weigh_weight) }}</span>
            </div>
          </div>
        </div>
        <div v-else class="empty-box">暂无趋势数据</div>
      </div>

      <!-- 右上：垃圾类型占比 -->
      <div class="panel-card">
        <div class="panel-header">
          <span>垃圾类型占比</span>
          <span class="panel-sub">按当前时间范围</span>
        </div>

        <div v-if="garbageItems.length" class="ratio-list">
          <div v-for="(item, index) in garbageItems" :key="index" class="ratio-row">
            <div class="ratio-name">{{ item.garbage_name || '-' }}</div>
            <div class="bar-bg">
              <div
                class="bar-inner orange"
                :style="{ width: `${Math.max(Number(item.weight_rate || 0) * 100, 5)}%` }"
              ></div>
            </div>
            <div class="ratio-percent">{{ formatPercent(item.weight_rate) }}</div>
            <div class="ratio-extra">{{ formatWeight(item.room_weight) }}kg</div>
          </div>
        </div>
        <div v-else class="empty-box">暂无垃圾类型数据</div>
      </div>

      <!-- 左下：小区排行 -->
      <div class="panel-card">
        <div class="panel-header">
          <span>小区回收排行 TOP{{ topN }}</span>
          <span class="panel-sub">按当前时间范围</span>
        </div>

        <div v-if="communityRank.length" class="rank-list">
          <div v-for="(item, index) in communityRank" :key="index" class="rank-row">
            <div class="rank-left">
              <span class="rank-badge">{{ item.rank || (index + 1) }}</span>
              <span class="rank-name" :title="item.community_name">{{ item.community_name || '-' }}</span>
            </div>
            <div class="rank-right">
              <span class="rank-count">{{ item.collect_count || 0 }}次</span>
              <span class="rank-weight">{{ formatWeight(item.room_weight) }}kg</span>
            </div>
          </div>
        </div>
        <div v-else class="empty-box">暂无小区排行数据</div>
      </div>

      <!-- 右下：车辆排行 -->
      <div class="panel-card">
        <div class="panel-header">
          <span>车辆清运排行 TOP{{ topN }}</span>
          <span class="panel-sub">按当前时间范围</span>
        </div>

        <div v-if="vehicleRank.length" class="rank-list">
          <div v-for="(item, index) in vehicleRank" :key="index" class="rank-row">
            <div class="rank-left rank-left-vehicle">
              <span class="rank-badge">{{ item.rank || (index + 1) }}</span>
              <div class="vehicle-info">
                <span class="rank-name" :title="item.vehicle_name">{{ item.vehicle_name || '-' }}</span>
                <span class="vehicle-sub">{{ item.plate_no || '-' }} / {{ item.driver_name || '-' }}</span>
              </div>
            </div>
            <div class="rank-right">
              <span class="rank-count">{{ item.collect_count || 0 }}次</span>
              <span class="rank-weight">{{ formatWeight(item.room_weight) }}kg</span>
            </div>
          </div>
        </div>
        <div v-else class="empty-box">暂无车辆排行数据</div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request'

export default {
  name: 'Home',
  data() {
    return {
      loadingAll: false,
      autoTimer: null,
      trendDays: 7,
      topN: 10,
      dateRange: [],

      overview: {
        device_total: 0,
        vehicle_total: 0,
        vehicle_online_total: 0,
        vehicle_collecting_total: 0,
        vehicle_idle_total: 0,
        collect_total: 0,
        collect_today: 0,
        room_weight_total: 0,
        room_weight_today: 0,
        weigh_weight_total: 0,
        weigh_weight_today: 0,
        weigh_unaudited_total: 0
      },

      trendItems: [],
      garbageItems: [],
      communityRank: [],
      vehicleRank: []
    }
  },

  created() {
    this.resetToday(false)
    this.fetchAllData()
    this.startAutoRefresh()
  },

  beforeDestroy() {
    this.clearAutoRefresh()
  },

  methods: {
    resetToday(needFetch = true) {
      const now = new Date()
      const start = new Date(now)
      start.setHours(0, 0, 0, 0)

      const end = new Date(now)
      end.setHours(23, 59, 59, 999)

      this.dateRange = [start.getTime(), end.getTime()]

      if (needFetch) {
        this.fetchAllData()
      }
    },

    async fetchAllData() {
      this.loadingAll = true
      try {
        await Promise.all([
          this.fetchOverview(),
          this.fetchCollectTrend(),
          this.fetchGarbageStats(),
          this.fetchCommunityRank(),
          this.fetchVehicleRank()
        ])
      } finally {
        this.loadingAll = false
      }
    },

    async fetchOverview() {
      try {
        const res = await request({
          url: '/wms/dashboard/overview',
          method: 'get'
        })
        const data = this.extractData(res)
        this.overview = Object.assign({}, this.overview, data || {})
      } catch (error) {
        console.error('获取总览统计失败', error)
      }
    },

    async fetchCollectTrend() {
      try {
        const res = await request({
          url: '/wms/dashboard/collect_trend',
          method: 'get',
          params: {
            days: this.trendDays
          }
        })
        const data = this.extractData(res)
        this.trendItems = Array.isArray(data.items) ? data.items : []
      } catch (error) {
        console.error('获取近7天清运趋势失败', error)
      }
    },

    async fetchGarbageStats() {
      try {
        const { startTime, endTime } = this.getSelectedRange()
        const res = await request({
          url: '/wms/dashboard/garbage_stats',
          method: 'get',
          params: {
            start_time: startTime,
            end_time: endTime
          }
        })
        const data = this.extractData(res)
        this.garbageItems = Array.isArray(data.items) ? data.items : []
      } catch (error) {
        console.error('获取垃圾类型占比失败', error)
      }
    },

    async fetchCommunityRank() {
      try {
        const { startTime, endTime } = this.getSelectedRange()
        const res = await request({
          url: '/wms/dashboard/community_rank',
          method: 'get',
          params: {
            top_n: this.topN,
            start_time: startTime,
            end_time: endTime
          }
        })
        const data = this.extractData(res)
        this.communityRank = Array.isArray(data.items) ? data.items : []
      } catch (error) {
        console.error('获取小区回收排行失败', error)
      }
    },

    async fetchVehicleRank() {
      try {
        const { startTime, endTime } = this.getSelectedRange()
        const res = await request({
          url: '/wms/dashboard/vehicle_rank',
          method: 'get',
          params: {
            top_n: this.topN,
            start_time: startTime,
            end_time: endTime
          }
        })
        const data = this.extractData(res)
        this.vehicleRank = Array.isArray(data.items) ? data.items : []
      } catch (error) {
        console.error('获取车辆清运排行失败', error)
      }
    },

    extractData(res) {
      const data = res && res.data ? res.data : res
      if (data && data.data) return data.data
      return data || {}
    },

    getSelectedRange() {
      if (this.dateRange && this.dateRange.length === 2) {
        return {
          startTime: Math.floor(this.dateRange[0] / 1000),
          endTime: Math.floor(this.dateRange[1] / 1000)
        }
      }

      const now = new Date()
      const start = new Date(now)
      start.setHours(0, 0, 0, 0)

      const end = new Date(now)
      end.setHours(23, 59, 59, 999)

      return {
        startTime: Math.floor(start.getTime() / 1000),
        endTime: Math.floor(end.getTime() / 1000)
      }
    },

    startAutoRefresh() {
      this.clearAutoRefresh()
      this.autoTimer = setInterval(() => {
        this.fetchAllData()
      }, 60000)
    },

    clearAutoRefresh() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer)
        this.autoTimer = null
      }
    },

    formatWeight(val) {
      const num = Number(val || 0)
      if (Number.isNaN(num)) return '0.0'
      return num.toFixed(1)
    },

    formatPercent(val) {
      const num = Number(val || 0)
      if (Number.isNaN(num)) return '0%'
      return `${(num * 100).toFixed(1)}%`
    },

    getBarWidth(value, arr, field) {
      const max = arr.length
        ? Math.max(...arr.map(item => Number(item[field] || 0)))
        : 0
      if (!max) return '0%'
      return `${Math.max((Number(value || 0) / max) * 100, 6)}%`
    }
  }
}
</script>

<style scoped>
.home-dashboard-page {
  min-height: 100%;
  background: #f5f7fb;
  padding: 12px;
  box-sizing: border-box;
}

.hero-panel {
  border-radius: 16px;
  background: linear-gradient(135deg, #0f172a 0%, #172554 100%);
  color: #fff;
  padding: 18px 20px;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.hero-left {
  min-width: 0;
}

.hero-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 6px;
}

.hero-subtitle {
  font-size: 13px;
  color: rgba(255,255,255,0.72);
}

.hero-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.date-range {
  width: 360px;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}

.overview-card {
  position: relative;
  overflow: hidden;
  padding: 16px 18px;
  border-radius: 14px;
  background: linear-gradient(135deg, #0f172a 0%, #18243c 100%);
  color: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}

.overview-card::after {
  content: '';
  position: absolute;
  right: -20px;
  top: -20px;
  width: 82px;
  height: 82px;
  background: rgba(59, 130, 246, 0.12);
  border-radius: 50%;
}

.overview-label {
  position: relative;
  z-index: 1;
  font-size: 13px;
  color: rgba(255,255,255,0.72);
  margin-bottom: 8px;
}

.overview-value {
  position: relative;
  z-index: 1;
  font-size: 28px;
  font-weight: 700;
}

.overview-value span {
  font-size: 12px;
  font-weight: 400;
}

.overview-value.success {
  color: #34d399;
}

.overview-value.warning {
  color: #fbbf24;
}

.overview-value.danger {
  color: #f87171;
}

.main-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  grid-template-rows: 430px 360px;
  gap: 12px;
}

.panel-card {
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid #e9eef7;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.panel-header {
  height: 52px;
  padding: 0 16px;
  border-bottom: 1px solid #edf2f7;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #0f172a;
  font-size: 15px;
  font-weight: 700;
}

.panel-sub {
  font-size: 12px;
  color: #64748b;
  font-weight: 400;
}

.trend-list,
.ratio-list,
.rank-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px 16px;
}

.trend-row {
  border-bottom: 1px dashed #e5e7eb;
  padding: 12px 0;
}

.trend-date {
  font-size: 13px;
  color: #334155;
  font-weight: 700;
  margin-bottom: 10px;
}

.trend-item {
  display: grid;
  grid-template-columns: 60px 1fr 70px;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;
}

.trend-item:last-child {
  margin-bottom: 0;
}

.trend-label {
  font-size: 12px;
  color: #64748b;
}

.bar-bg {
  width: 100%;
  height: 10px;
  border-radius: 6px;
  background: #e9eef7;
  overflow: hidden;
}

.bar-inner {
  height: 100%;
  border-radius: 6px;
}

.bar-inner.blue {
  background: linear-gradient(90deg, #60a5fa 0%, #2563eb 100%);
}

.bar-inner.cyan {
  background: linear-gradient(90deg, #67e8f9 0%, #06b6d4 100%);
}

.bar-inner.green {
  background: linear-gradient(90deg, #6ee7b7 0%, #10b981 100%);
}

.bar-inner.orange {
  background: linear-gradient(90deg, #fdba74 0%, #f97316 100%);
}

.trend-value,
.ratio-percent,
.ratio-extra {
  font-size: 12px;
  color: #0f172a;
  font-weight: 600;
  text-align: right;
}

.ratio-row {
  display: grid;
  grid-template-columns: 70px 1fr 60px 80px;
  gap: 10px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px dashed #e5e7eb;
}

.ratio-name {
  font-size: 13px;
  color: #334155;
  font-weight: 700;
}

.rank-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px dashed #e5e7eb;
}

.rank-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.rank-left-vehicle {
  align-items: flex-start;
}

.rank-badge {
  width: 24px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  border-radius: 50%;
  background: #1d4ed8;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.rank-name {
  font-size: 14px;
  color: #0f172a;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vehicle-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.vehicle-sub {
  font-size: 12px;
  color: #64748b;
}

.rank-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.rank-count {
  font-size: 12px;
  color: #64748b;
}

.rank-weight {
  font-size: 13px;
  color: #2563eb;
  font-weight: 700;
}

.empty-box {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 13px;
  padding: 20px;
}

@media screen and (max-width: 1600px) {
  .overview-grid {
    grid-template-columns: repeat(4, 1fr);
  }

  .main-grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }
}

@media screen and (max-width: 1366px) {
  .overview-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .date-range {
    width: 320px;
  }
}
</style>